<template>
    <component
        :is="isMobileView && type !== ProductCardType['MINI_HORIZONTAL'] ? 'a' : 'div'"
        v-if="productData"
        class="hotel-product-card"
        :class="cardClasses"
        :href="isMobileView && type !== ProductCardType['MINI_HORIZONTAL'] ? productData.info.url : null"
        :data-product-id="productData.hotel_id"
        external
    >
        <div class="image" :style="optimizedImage">
            <LazyClientOnly>
                <NuxtImg
                    :src="optimizedImage.large"
                    :srcset="`${optimizedImage.large}, ${optimizedImage.medium}`"
                    alt="Product Image"
                    loading="lazy"
                    :style="{ 'object-fit': 'cover', width: '100%', height: '100%' }"
                    densities="x1"
                />
            </LazyClientOnly>
        </div>
        <div class="content">
            <div class="info">
                <div class="info-container">
                    <component
                        :is="isMobileView ? 'h5' : 'a'"
                        class="title"
                        :href="isMobileView ? null : relativeLink(productLink)"
                        external
                    >
                        {{ productData.name }}
                    </component>
                    <div
                        v-if="productData.overview && productData.overview.rating_by_customers.accurate > 0"
                        class="rating"
                    >
                        <StarsRating :value="productData.overview.rating_by_customers.outOfFiveFraction"></StarsRating>
                        <div class="rating-info">
                            <span v-if="ratingAccurate > 4.25" class="rating-word">{{
                                $t('ratingWords.excellent')
                            }}</span>
                            <span v-else-if="ratingAccurate > 3.75" class="rating-word">{{
                                $t('ratingWords.great')
                            }}</span>
                            <span v-else-if="ratingAccurate > 3.25" class="rating-word">{{
                                $t('ratingWords.good')
                            }}</span>
                            <span v-else-if="ratingAccurate === 3.25" class="rating-word">{{
                                $t('ratingWords.average')
                            }}</span>
                            <span v-else class="rating-word"></span>
                            <span class="review-count"
                                >({{ ratingAccurate }}, {{ productData.overview.surveys_count }}
                                {{ $t('reviews') }})</span
                            >
                        </div>
                    </div>
                    <div class="details-container">
                        <div class="details">
                            <div
                                v-if="type === ProductCardType['VERTICAL'] && siteInfo.site === 'APR'"
                                class="distance"
                            >
                                <NuxtIcon name="ri:price-tag-3-fill" class="distance-icon" />
                                {{ $t('aprPackagesDeal.packageDealStatus') }}
                            </div>
                            <div v-if="productData.overview && distanceToAirport" class="distance">
                                <NuxtIcon
                                    :name="`ri:${airportTypeIcon}`"
                                    class="distance-icon"
                                    :class="{ transformed: airportTypeIcon === 'plane-fill' }"
                                />
                                {{ distanceToAirport }} {{ $t('milesFrom').toLowerCase() }}
                                {{ airportData.content?.code || productData.airportCode }}
                            </div>
                            <div v-if="type === ProductCardType['HORIZONTAL']" class="benefits">
                                <div v-if="productData?.overview?.parking_max" class="benefit">
                                    <NuxtIcon name="ri:check-line" class="benefit-icon" />
                                    {{ $t('packageIncludesParking') }}
                                </div>
                                <div v-if="productData?.info?.shuttle_free" class="benefit">
                                    <NuxtIcon name="ri:check-line" class="benefit-icon" />
                                    {{ isAirportPage ? $t('airportShuttleIncluded') : $t('cruiseShuttleIncluded') }}
                                </div>
                                <div class="benefit">
                                    <NuxtIcon name="ri:check-line" class="benefit-icon" />
                                    {{ $t('freeCancellation') }}
                                </div>
                            </div>
                            <div
                                v-if="
                                    (type === ProductCardType['MINI_HORIZONTAL'] ||
                                        type === ProductCardType['VERTICAL']) &&
                                    productData.info?.shuttle_free
                                "
                                class="free-shuttle"
                            >
                                <NuxtIcon name="ri:bus-fill" class="free-shuttle-icon" />
                                {{ $t('freeShuttle') }}
                            </div>
                        </div>
                        <div v-if="type === ProductCardType['MINI_HORIZONTAL']" class="button-container">
                            <nuxt-link :to="relativeLink(productLink)" class="link-button" external>{{
                                $t('viewDeal')
                            }}</nuxt-link>
                        </div>
                    </div>
                </div>
                <div v-if="type != ProductCardType['MINI_HORIZONTAL']" class="button-container">
                    <div v-if="isMobileView && type === ProductCardType['HORIZONTAL']" class="text-button">
                        {{ $t('getRates') }}
                    </div>
                    <div v-else-if="isMobileView && type === ProductCardType['VERTICAL']" class="link-button">
                        {{ $t('viewDeal') }}
                    </div>
                    <nuxt-link v-else :to="relativeLink(productLink)" class="link-button" external>{{
                        $t('viewDeal')
                    }}</nuxt-link>
                </div>
                <div
                    v-if="type === ProductCardType['VERTICAL']"
                    :class="['free-cancellation', { 'pb-5': productData.freeCancellation === false }]"
                >
                    {{ (productData.freeCancellation ?? true) ? $t('freeCancellation') : '' }}
                </div>
            </div>
            <p v-if="type === ProductCardType['HORIZONTAL']" class="description">
                {{ displayedDescription }}
                <span v-if="showShowMore" class="read-more" @click="expandDescription = !expandDescription">
                    {{ expandDescription ? $t('readLess') : $t('readMore') }}
                </span>
            </p>
        </div>
    </component>
</template>

<script setup lang="ts">
import StarsRating from '@nuxt-monorepo/ui/components/common/StarsRating.vue';
import { useIsMobileView } from '@nuxt-monorepo/ui/composables/isMobileView';

enum ProductCardType {
    VERTICAL = 'vertical',
    HORIZONTAL = 'horizontal',
    MINI_HORIZONTAL = 'mini-horizontal',
}

const props = defineProps<{
    blok: any;
    mobileBreakpoint: string;
    productData: any;
    airportData?: any;
    type: ProductCardType;
}>();

const img = useImage();
const config = useRuntimeConfig();

const siteInfo = inject('baseInfo');
const ratingAccurate = parseFloat(props.productData.overview.rating_by_customers.accurate?.toFixed(1));
const airportTypeIcon = props.airportData.content.type === 'port' ? 'ship-fill' : 'plane-fill';

// Hotel product card type class (horizontal, vertical, mini-horizontal, etc)
const cardClasses = computed(() => {
    return [props.type];
});

const expandDescription = ref(false);
const descriptionCharacterLimit = 222;

// Variable to display cropped or full description
const displayedDescription = computed(() => {
    const description = props.productData.info.description2 || props.productData.info.description;
    if (showShowMore.value) {
        return expandDescription.value ? description : description.slice(0, descriptionCharacterLimit) + '...';
    }

    return description;
});

// Variable to hide\display show-more button
const showShowMore = computed(() => {
    const description = props.productData.info.description2 || props.productData.info.description;
    if (description) {
        return description.length > descriptionCharacterLimit;
    } else {
        return false;
    }
});

const optimizedImage = computed(() => {
    const largeImgUrl = img(`${props.productData.images.large_url}/${props.productData.images?.data[0]}`, {
        format: 'webp',
        height: 200,
    });
    const mediumImgUrl = img(`${props.productData.images.medium_url}/${props.productData.images?.data[0]}`, {
        format: 'webp',
        height: 200,
    });
    return { large: largeImgUrl, medium: mediumImgUrl };
});

const { isMobileView } = useIsMobileView(props.mobileBreakpoint);

const route = useRoute();
const isAirportPage = computed(() => {
    return route.path.includes('airport');
});

const productLink = computed(
    () => props.productData.info.url || `${config.public.SITE_URL}/${props.productData.content_root.slug}`
);
const distanceToAirport = computed(() =>
    (props.productData.overview.distance_to_airport || props.productData.distance)?.toFixed(1)
);

function relativeLink(link: string) {
    const fullUrl = new URL(link);
    return fullUrl.pathname;
}
</script>

<style lang="postcss" scoped>
.hotel-product-card {
    @apply text-xs font-light text-gray-800 no-underline relative flex bg-white rounded overflow-hidden w-full h-full;
    word-wrap: break-word;

    a.title {
        @apply inline-block;
        &:hover {
            @apply underline;
        }
    }
    .rating-stars {
        @apply inline-block align-middle;
    }
    .rating-info {
        @apply inline-block relative;
    }
    .rating-word {
        @apply font-semibold mx-1 sm:mx-2;
    }
    .distance {
        @apply flex items-center mb-1 sm:text-sm sm:font-normal;
    }
    .free-shuttle {
        @apply flex items-center sm:font-normal;
        .iconify {
            @apply mr-1;
        }
    }
    .distance-icon {
        @apply text-sm mr-1;
        &.transformed {
            transform: rotate(45deg);
        }
    }
    .benefit {
        @apply flex items-center mb-1 sm:text-sm sm:font-normal;
    }
    .text-button {
        @apply font-semibold text-orange-500;
    }
    .description {
        @apply mt-2 mb-0 text-sm hidden sm:block;
    }
    .read-more {
        @apply text-primary font-normal capitalize cursor-pointer;
    }

    &.horizontal,
    &.vertical {
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
        transition: all 0.5s ease-in-out;
        @media screen(sm) {
            &:hover {
                box-shadow: 0 3px 6px 0 rgba(41, 63, 77, 0.6);
            }
        }

        @apply flex-col;
        .image {
            @apply bg-center bg-cover bg-gray-300 w-full;
        }
        .title {
            @apply mb-1 text-sm font-semibold;
        }
        .rating {
            @apply mb-1 text-sm leading-4 font-normal;
        }
        .rating-stars {
            @apply my-0.5 text-base;
            .iconify {
                @apply mr-0.5;
                &:last-child {
                    @apply mr-0;
                }
            }
        }
        .benefit-icon {
            @apply text-secondary text-base mr-1;
        }
        .link-button {
            @apply sm:inline-block rounded text-lg font-medium cursor-pointer px-6 py-2 text-white bg-btn border-btn-border;
        }
    }

    &.horizontal {
        @apply sm:flex-row flex-wrap;

        .image {
            @apply min-h-[10rem] h-full min-w-[35%] flex-1 rounded-ss rounded-se;
            @media screen(sm) {
                @apply h-auto flex flex-shrink-0 rounded-s rounded-se-none;
            }
        }
        .title {
            @apply sm:font-medium sm:text-lg;
        }
        .content {
            @apply pt-4 px-4 pb-2 sm:flex-[355px];
            @apply sm:pt-8 sm:px-8 sm:pb-6 sm:flex-grow;
        }
        .rating-word {
            @apply font-semibold mx-1 sm:mx-2;
        }
        .info {
            @apply sm:flex sm:justify-between sm:flex-wrap;
        }
        .info-container {
            @apply sm:leading-7 sm:flex-[75%];
        }
        .benefits {
            @apply sm:flex sm:flex-wrap;
        }
        .benefit {
            @apply sm:text-sm sm:mb-0 sm:mr-4;
        }
        .button-container {
            @apply absolute right-4 bottom-3 whitespace-nowrap sm:flex-[100px];
            @apply sm:static sm:my-4;
        }
        .link-button {
            @apply sm:inline-block rounded text-lg font-medium cursor-pointer px-6 py-2 text-white bg-btn border-btn-border;

            &:hover {
                @apply bg-btn-hover border-btn-borderHover;
            }
        }
    }

    &.mini-horizontal {
        @apply items-stretch sm:flex sm:flex-wrap;
        border: 1px solid rgba(0, 0, 0, 0.125);

        .image {
            flex: 0 0 107px;
            @apply sm:min-h-[10rem] sm:min-w-[33%] bg-center bg-cover bg-no-repeat bg-gray-300 flex-shrink-0 sm:flex-1;
        }
        .title {
            @apply font-normal leading-5 mb-2;
            font-size: 17px;
        }
        .content {
            @apply p-3 flex-grow sm:flex-[330px] sm:p-4;
        }
        .details-container {
            @apply flex flex-wrap;
        }
        .details {
            @apply pr-2;
        }
        .rating {
            @apply text-xs mb-3 sm:text-sm;
        }
        .rating-stars {
            @media screen(sm) {
                @apply text-base;
                .iconify {
                    @apply mr-0.5;
                    &:last-child {
                        @apply mr-0;
                    }
                }
            }
        }
        .rating-word {
            @apply font-semibold mx-1 sm:mx-2;
        }
        .distance,
        .free-shuttle {
            @apply text-sm font-normal;
            .iconify {
                @apply mr-1.5 sm:text-base;
            }
        }
        .distance {
            @apply mb-2;
        }
        .button-container {
            @apply flex flex-col justify-end items-end flex-1 pt-1.5 sm:mt-0;
        }
        .link-button {
            @apply text-btn text-sm font-normal font-semibold bg-none whitespace-nowrap;
            &:hover {
                @apply underline bg-none;
            }
            @media screen(sm) {
                @apply py-1 px-5 text-white bg-btn rounded text-lg h-10 transition-all text-center;
                min-width: 132px;
                &:hover {
                    @apply no-underline bg-btn-hover;
                }
            }
        }
    }

    &.vertical {
        @apply text-sm;
        .image {
            @apply h-44;
            @media screen(sm) {
                height: 200px;
            }
        }
        .content {
            @apply p-3 flex flex-grow flex-col;
        }
        .info {
            @apply flex flex-grow flex-col;
        }
        .info-container {
            @apply mb-5;
        }
        .title {
            @apply font-normal;
            font-size: 17px;
        }
        .rating-word {
            @apply mx-2 font-semibold;
        }
        .distance {
            @apply mb-2.5;
        }
        .button-container {
            @apply flex justify-center mt-auto;
        }
        .link-button {
            @apply py-1 inline-flex justify-center text-center bg-btn;
            min-width: 198px;
            &:hover {
                @apply bg-btn-hover;
            }
        }
        .free-cancellation {
            @apply text-center text-green-600 text-sm font-semibold mt-2;
        }
    }
}
</style>
